import { databaseService } from './database';
import { getPlatformServices } from './platform';
import { PCInstance } from '../types/database';

/**
 * Wake machines over BOTH available paths, because neither one covers the case
 * the other does:
 *
 *  1. The DB flag. Online RealityAgent peers on the target's network poll
 *     wake_requested and send magic packets until it boots. This is the path that
 *     works from off-site — but it needs at least one agent already up on that
 *     network to act on the flag.
 *
 *  2. A magic packet straight from this operator device. This is the path that
 *     works when the whole room is dark — which is exactly when path 1 has nobody
 *     to act on the flag, and exactly the state a room is in every morning.
 *
 * Sending both is deliberate rather than redundant: WOL is idempotent and costs
 * nothing, and either path alone leaves a real scenario broken. Path 2 is
 * unavailable on the web build (no raw sockets) and only reaches networks this
 * device can broadcast to, so path 1 is never dropped.
 *
 * Returns whether the DB flag was written; local sends are best-effort and are
 * only logged, since a failure there still leaves path 1 in play.
 */
export async function wakeMachines(targets: PCInstance[]): Promise<boolean> {
  if (targets.length === 0) return true;

  const flagged = await databaseService.requestWake(targets.map((pc) => pc.id));

  const wol = getPlatformServices().wol;
  if (!wol) {
    // Web build: no local socket access. Peer agents are the only route, so a
    // fully-dark room cannot be woken from here.
    console.warn('[wake] No local WOL on this platform — relying on peer agents.');
    return flagged;
  }

  await Promise.all(
    targets
      .filter((pc) => pc.mac_address)
      .map((pc) =>
        wol
          .send(pc.mac_address, pc.ip_address || '')
          .catch((e) => console.warn(`[wake] Local WOL to ${pc.pc_name || pc.id} failed:`, e))
      )
  );

  return flagged;
}
